import React from "react";
import type {
  ComponentBaseContext,
  ComponentBaseMapper,
  CustomFieldRenderer,
  EventStateRegistry,
  ExecutionDetailsContext,
  ExecutionInfo,
  NodeInfo,
  OutputPayload,
  StateFunction,
  SubtitleContext,
} from "../types";
import type { ComponentBaseProps, EventSection, EventState, EventStateMap } from "@/ui/componentBase";
import { DEFAULT_EVENT_STATE_MAP } from "@/ui/componentBase/eventState";
import { getTriggerRenderer, getState, getStateMap } from "../mapperLookup";
import { TimeLeftCountdown } from "@/ui/timeLeftCountdown";
import { calcRelativeTimeFromDiff, formatTimestamp } from "@/lib/utils";
import type { MetadataItem } from "@/ui/metadataList";
import { renderTimeAgo, renderWithTimeAgo } from "@/components/TimeAgo";
import { PushThroughHandler } from "@/pages/app/components/PushThroughHandler";
import { ExpressionEnvironment, ExpressionExamples, ExpressionTooltip } from "./expressionHelp";

// Helper function to detect if a value contains expressions
function hasExpressions(value: string): boolean {
  return typeof value === "string" && value.includes("{{") && value.includes("}}");
}

export const waitMapper: ComponentBaseMapper = {
  props(context: ComponentBaseContext): ComponentBaseProps {
    const componentName = context.componentDefinition.name || "wait";
    const lastExecution = context.lastExecutions.length > 0 ? context.lastExecutions[0] : null;

    return {
      iconSlug: context.componentDefinition.icon || "circle-off",
      iconColor: "text-gray-800",
      metadata: getWaitMetadataList(context.node),
      collapsed: context.node.isCollapsed,
      collapsedBackground: "bg-white",
      title:
        context.node.name ||
        context.componentDefinition.label ||
        context.componentDefinition.name ||
        "Unnamed component",
      eventSections: lastExecution
        ? getWaitEventSections(
            context.nodes,
            lastExecution,
            context.node.configuration as WaitConfiguration,
            componentName,
          )
        : undefined,
      includeEmptyState: !lastExecution,
      hideMetadataList: false,
      eventStateMap: getStateMap(componentName),
      customField: getWaitCustomField(context),
      customFieldVisibility: "live-only",
    };
  },

  subtitle(context: SubtitleContext): React.ReactNode {
    const subtitle = getWaitEventSubtitle(context.execution, context.node.configuration as WaitConfiguration, "wait");
    return subtitle || "";
  },

  getExecutionDetails(context: ExecutionDetailsContext): Record<string, unknown> {
    const details: Record<string, unknown> = {};
    const outputs = context.execution.outputs as { default?: OutputPayload[] } | undefined;
    const payload = outputs?.default?.[0];
    const data = payload?.data as WaitOutputData | undefined;
    const actor = data?.actor;
    const metadata = context.execution.metadata as { interval_duration?: number; start_time?: string } | undefined;

    const startedAt = formatDateValue(data?.started_at) || formatDateValue(metadata?.start_time);
    if (startedAt) {
      details["Started At"] = startedAt;
    }

    const finishedAt = formatDateValue(data?.finished_at);
    if (finishedAt) {
      details["Finished At"] = finishedAt;
    }

    if (data?.result) {
      details["Result"] = String(data.result);
    }

    if (data?.reason) {
      details["Reason"] = String(data.reason);
    }

    if (actor?.display_name || actor?.email) {
      details["Actor"] = actor.display_name || actor.email || "";
    }

    if (metadata?.interval_duration && metadata.interval_duration > 0) {
      details["Interval Duration"] = calcRelativeTimeFromDiff(metadata.interval_duration);
    }

    return details;
  },
};

function getWaitCustomField(context: ComponentBaseContext): React.ReactNode {
  const lastExecution = context.lastExecutions.length > 0 ? context.lastExecutions[0] : null;
  if (!lastExecution) {
    return null;
  }

  if (lastExecution.state === "STATE_FINISHED") {
    return null;
  }

  return React.createElement(PushThroughHandler, {
    onPushThrough: async () => {
      if (!lastExecution?.id) return;
      return context.actions.invokeNodeExecutionHook(lastExecution.id, "pushThrough", null);
    },
  });
}

export const WAIT_STATE_MAP: EventStateMap = {
  ...DEFAULT_EVENT_STATE_MAP,
  cancelling: {
    icon: "refresh-cw",
    textColor: "text-gray-800",
    backgroundColor: "bg-amber-100",
    badgeColor: "bg-amber-500",
    label: "Cancelling",
  },
  finished: {
    icon: "circle-check",
    textColor: "text-gray-800",
    backgroundColor: "bg-green-100",
    badgeColor: "bg-emerald-500",
  },
  "pushed through": {
    icon: "arrow-right",
    textColor: "text-gray-800",
    backgroundColor: "bg-amber-100",
    badgeColor: "bg-amber-500",
  },
};

export const waitStateFunction: StateFunction = (execution: ExecutionInfo): EventState => {
  if (!execution) return "neutral";

  if (
    execution.resultMessage &&
    (execution.resultReason === "RESULT_REASON_ERROR" ||
      (execution.result === "RESULT_FAILED" && execution.resultReason !== "RESULT_REASON_ERROR_RESOLVED"))
  ) {
    return "error";
  }

  if (execution.result === "RESULT_CANCELLED") {
    return "cancelled";
  }

  if (execution.state === "STATE_CANCELLING") {
    return "cancelling";
  }

  if (execution.state === "STATE_PENDING" || execution.state === "STATE_STARTED") {
    return "running";
  }

  if (execution.state === "STATE_FINISHED" && execution.result === "RESULT_PASSED") {
    const outputs = execution.outputs as { default?: OutputPayload[] } | undefined;
    const payload = outputs?.default?.[0];
    const data = payload?.data as Record<string, unknown> | undefined;
    const reason = data?.reason;

    if (reason === "manual_override") {
      return "pushed through";
    }

    return "finished";
  }

  return "failed";
};

export const WAIT_STATE_REGISTRY: EventStateRegistry = {
  stateMap: WAIT_STATE_MAP,
  getState: waitStateFunction,
};

function getWaitMetadataList(node: NodeInfo): MetadataItem[] {
  const configuration = node.configuration as Record<string, unknown>;

  // Handle new mode-based configuration
  if (configuration?.mode) {
    const mode = configuration.mode as string;
    let waitLabel: string | React.ReactNode = "Wait not configured";

    if (mode === "interval") {
      const waitFor = configuration.waitFor as string;
      const unit = configuration.unit as string;

      if (hasExpressions(waitFor)) {
        waitLabel = (
          <span>
            Wait for{" "}
            <ExpressionTooltip expression={waitFor}>
              <span className="underline decoration-dotted cursor-help">Internal Expression</span>
            </ExpressionTooltip>
          </span>
        );
      } else if (waitFor && unit) {
        waitLabel = `Wait for ${waitFor}${unit ? ` ${unit}` : ""}`;
      }
    } else if (mode === "countdown") {
      const waitUntil = configuration.waitUntil as string;

      if (hasExpressions(waitUntil)) {
        waitLabel = (
          <span>
            Wait until{" "}
            <ExpressionTooltip expression={waitUntil}>
              <span className="underline decoration-dotted cursor-help">Internal Expression</span>
            </ExpressionTooltip>
          </span>
        );
      } else if (waitUntil) {
        waitLabel = `Wait until: ${waitUntil}`;
      }
    }

    return [
      {
        icon: "loader",
        label: waitLabel,
      },
    ];
  }

  // Handle legacy duration configuration for backward compatibility
  const duration = configuration?.duration as { value: number; unit: "seconds" | "minutes" | "hours" };
  if (duration) {
    return [
      {
        icon: "loader",
        label: `Wait for: ${duration.value} ${duration.unit}`,
      },
    ];
  }

  // Fallback for no configuration
  return [
    {
      icon: "loader",
      label: "Wait not configured",
    },
  ];
}

function getWaitEventSections(
  nodes: NodeInfo[],
  execution: ExecutionInfo,
  configuration: WaitConfiguration,
  componentName: string,
): EventSection[] {
  const executionState = getState(componentName)(execution);
  const rootTriggerNode = nodes.find((n) => n.id === execution.rootEvent?.nodeId);
  const rootTriggerRenderer = getTriggerRenderer(rootTriggerNode?.componentName || "");
  const { title } = rootTriggerRenderer.getTitleAndSubtitle({ event: execution.rootEvent });

  const eventSubtitle = getWaitEventSubtitle(execution, configuration, componentName);

  const eventSection: EventSection = {
    receivedAt: new Date(execution.createdAt!),
    eventTitle: title,
    eventSubtitle,
    eventState: executionState,
    eventId: execution.rootEvent!.id!,
  };

  return [eventSection];
}

type WaitOutputData = {
  started_at?: string;
  finished_at?: string;
  result?: unknown;
  reason?: unknown;
  actor?: {
    email?: string;
    display_name?: string;
  };
};

type WaitConfiguration = {
  mode: "interval" | "countdown";
  unit?: "seconds" | "minutes" | "hours";
  waitFor?: string;
  waitUntil?: string;
  duration?: { value: number; unit: "seconds" | "minutes" | "hours" };
};

function getWaitEventSubtitle(
  execution: ExecutionInfo,
  configuration: WaitConfiguration,
  componentName: string,
): string | React.ReactNode | undefined {
  const executionState = getState(componentName)(execution);
  const timeAgoDate = execution.updatedAt
    ? new Date(execution.updatedAt)
    : execution.createdAt
      ? new Date(execution.createdAt)
      : null;

  // Get expected duration from execution metadata (calculated interval)
  let expectedDuration: number | undefined;

  if (execution.metadata) {
    try {
      const metadata = execution.metadata as { interval_duration?: number };
      if (metadata.interval_duration && metadata.interval_duration > 0) {
        expectedDuration = metadata.interval_duration;
      }
    } catch {
      // If parsing metadata fails, fall back to configuration-based calculation
    }
  }

  // Fallback to configuration-based calculation if metadata is not available
  if (!expectedDuration && configuration) {
    if (configuration?.mode === "interval") {
      const waitFor = configuration.waitFor as string;
      const unit = configuration.unit as string;

      // Try to parse waitFor as a number (for simple cases)
      const value = parseInt(waitFor, 10);
      if (!isNaN(value) && unit) {
        const multipliers = { seconds: 1000, minutes: 60000, hours: 3600000 };
        expectedDuration = value * (multipliers[unit as keyof typeof multipliers] || 1000);
      }
    } else if (configuration?.mode === "countdown") {
      expectedDuration = countdownDurationFromConfig(configuration.waitUntil, execution.createdAt);
    } else if (configuration?.duration) {
      // Legacy duration format
      const duration = configuration.duration as { value: number; unit: "seconds" | "minutes" | "hours" };
      const { value, unit } = duration;
      const multipliers = { seconds: 1000, minutes: 60000, hours: 3600000 };
      expectedDuration = value * (multipliers[unit as keyof typeof multipliers] || 1000);
    }
  }

  if (executionState === "running" && execution.createdAt && expectedDuration) {
    return (
      <>
        <TimeLeftCountdown createdAt={new Date(execution.createdAt)} expectedDuration={expectedDuration} />
        {timeAgoDate ? (
          <>
            {" · "}
            {renderTimeAgo(timeAgoDate)}
          </>
        ) : (
          ""
        )}
      </>
    );
  }

  if (executionState === "finished" || executionState === "failed" || executionState === "pushed through") {
    if (execution.updatedAt) {
      const doneAtText = `Done at: ${formatTimestamp(new Date(execution.updatedAt))}`;
      return renderWithTimeAgo(doneAtText, new Date(execution.updatedAt));
    }
    return timeAgoDate ? renderWithTimeAgo("Done", timeAgoDate) : "Done";
  }

  return timeAgoDate ? renderTimeAgo(timeAgoDate) : undefined;
}

function countdownDurationFromConfig(waitUntil?: string, createdAt?: string): number | undefined {
  if (!waitUntil || !createdAt) {
    return undefined;
  }

  try {
    const dateMatch = waitUntil.match(/["']([^"']+)["']/);
    if (!dateMatch) {
      return undefined;
    }

    const targetDate = new Date(dateMatch[1]);
    const createdDate = new Date(createdAt);
    if (Number.isNaN(targetDate.getTime()) || Number.isNaN(createdDate.getTime())) {
      return undefined;
    }

    return targetDate.getTime() - createdDate.getTime();
  } catch {
    return undefined;
  }
}

function formatDateValue(value?: string): string | undefined {
  if (!value) return undefined;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString();
}

/**
 * Custom field renderer for wait component configuration
 */
export const waitCustomFieldRenderer: CustomFieldRenderer = {
  render: (node: NodeInfo) => {
    const configuration = node.configuration as WaitConfiguration;
    const mode = configuration.mode;

    let content: React.ReactNode;
    let title: string;

    if (mode === "interval") {
      title = "Fixed Time Interval";
      content = (
        <div className="space-y-2">
          <p>Component will wait for a fixed amount of time before emitting the event forward.</p>
          <p>Supports expressions and expects integer.</p>
          <ExpressionEnvironment />
          <ExpressionExamples
            examples={[
              '{{ $["Node Name"].data.wait_seconds }}',
              "{{ root().data.policy.delay_minutes }}",
              "{{ previous().data.retry_after_seconds }}",
              "{{ previous(2).data.backoff_seconds }}",
              '{{ $["Node Name"].data.priority == "urgent" ? 0 : 30 }}',
              '{{ int($["Node Name"].data.delay_seconds) }}',
            ]}
          />
          <p>Check Docs for more details on selecting data from payloads and expressions.</p>
        </div>
      );
    } else if (mode === "countdown") {
      title = "Countdown to Date/Time";
      content = (
        <div className="space-y-2">
          <p>Component will countdown until the provided date/time before emitting an event forward.</p>
          <p>Supports expressions and expects date in ISO 8601 format.</p>
          <ExpressionEnvironment />
          <ExpressionExamples
            examples={[
              '{{ $["Node Name"].data.run_at }}',
              "{{ root().data.schedule.start_at }}",
              '{{ previous().data["Scheduler"].data.next_run_at }}',
              '{{ date($["Node Name"].data.start_at).Add(duration("2h")).Format("2006-01-02T15:04:05Z") }}',
              '{{ now().Add(duration("24h")).Format("2006-01-02") }}',
              '{{ date(previous(3).data["Creator"].data.created_at).In(timezone("America/Chicago")) }}',
            ]}
          />
          <p>Check Docs for more details on selecting data from payloads and expressions.</p>
        </div>
      );
    } else {
      title = "Wait Component";
      content = "Configure the wait mode to see more details.";
    }

    return (
      <div className="border-t-1 border-gray-200 pt-4">
        <div className="space-y-3">
          <div>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{title}:</span>
            <div className="text-xs text-gray-800 dark:text-gray-100 mt-1 border-1 border-gray-200 dark:border-gray-600 px-2.5 py-2 bg-white dark:bg-gray-900 rounded-md">
              {content}
            </div>
          </div>
        </div>
      </div>
    );
  },
};
